import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Paper, Box, Grid, Card, 
  CardContent, Button, FormControl, InputLabel, 
  Select, MenuItem, SelectChangeEvent, RadioGroup,
  FormControlLabel, Radio, Divider, Stack, Alert
} from '@mui/material';
import { 
  PictureAsPdf as PdfIcon, 
  Description as WordIcon,
  Assignment as ProjectIcon,
  Business as PortfolioIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import api from '../api/config';
import { 
  getProjectReportPdfUrl, 
  getProjectReportWordUrl,
  getPortfolioReportPdfUrl,
  getPortfolioReportWordUrl
} from '../api/projectAnalyticsService';
import { getProjects } from '../api/projectService';

const ReportsPage: React.FC = () => {
  const [reportLevel, setReportLevel] = useState<'project' | 'portfolio'>('project');
  const [reportFormat, setReportFormat] = useState<'pdf' | 'word'>('pdf');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await getProjects();
        setProjects(data);
        if (data && data.length > 0) {
          const firstProject = data[0];
          if (firstProject && firstProject.id !== undefined && firstProject.id !== null) {
            setSelectedProject(String(firstProject.id));
          }
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, []);
  
  const handleLevelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReportLevel(event.target.value as 'project' | 'portfolio');
  };
  
  const handleFormatChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReportFormat(event.target.value as 'pdf' | 'word');
  };
  
  const handleProjectChange = (event: SelectChangeEvent) => {
    setSelectedProject(event.target.value);
  };
  
  const handleDownload = () => {
    let url = '';
    
    if (reportLevel === 'project') {
      if (!selectedProject) return;
      const projectId = parseInt(selectedProject);
      url = reportFormat === 'pdf' ? getProjectReportPdfUrl(projectId) : getProjectReportWordUrl(projectId);
    } else {
      url = reportFormat === 'pdf' ? getPortfolioReportPdfUrl() : getPortfolioReportWordUrl();
    }
    
    // Use baseURL from api config to ensure consistency
    const apiBaseUrl = api.defaults.baseURL || '';
    // Ensure we don't have double slashes
    const cleanBaseUrl = apiBaseUrl.replace(/\/$/, '');
    const cleanUrl = url.startsWith('/') ? url : `/${url}`;
    const fullUrl = `${cleanBaseUrl}${cleanUrl}`;
    
    console.log("Downloading report from:", fullUrl);
    window.open(fullUrl, '_blank');
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
          Zandrigo Intelligence Reports
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto', fontWeight: 400 }}>
          Generate comprehensive analytics and executive summaries in multiple professional formats.
        </Typography>
      </Box>
      
      <Grid container spacing={4} justifyContent="center">
        <Grid {...{ item: true, xs: 12, md: 8 }}>
          <Paper elevation={0} sx={{ p: 5, borderRadius: 5, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, mb: 4 }}>Configure Your Report</Typography>
            
            <Box sx={{ mb: 5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>1. Select Report Level</Typography>
              <RadioGroup row value={reportLevel} onChange={handleLevelChange}>
                <Grid container spacing={2}>
                  <Grid {...{ item: true, xs: 12, sm: 6 }}>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        borderRadius: 3, 
                        cursor: 'pointer',
                        borderColor: reportLevel === 'project' ? 'primary.main' : 'divider',
                        bgcolor: reportLevel === 'project' ? 'rgba(37, 99, 235, 0.04)' : 'transparent',
                        '&:hover': { borderColor: 'primary.main' }
                      }}
                      onClick={() => setReportLevel('project')}
                    >
                      <FormControlLabel 
                        value="project" 
                        control={<Radio />} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ProjectIcon sx={{ mr: 1, color: reportLevel === 'project' ? 'primary.main' : 'text.secondary' }} />
                            <Typography sx={{ fontWeight: 600 }}>Individual Project</Typography>
                          </Box>
                        } 
                      />
                    </Paper>
                  </Grid>
                  <Grid {...{ item: true, xs: 12, sm: 6 }}>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        borderRadius: 3, 
                        cursor: 'pointer',
                        borderColor: reportLevel === 'portfolio' ? 'primary.main' : 'divider',
                        bgcolor: reportLevel === 'portfolio' ? 'rgba(37, 99, 235, 0.04)' : 'transparent',
                        '&:hover': { borderColor: 'primary.main' }
                      }}
                      onClick={() => setReportLevel('portfolio')}
                    >
                      <FormControlLabel 
                        value="portfolio" 
                        control={<Radio />} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PortfolioIcon sx={{ mr: 1, color: reportLevel === 'portfolio' ? 'primary.main' : 'text.secondary' }} />
                            <Typography sx={{ fontWeight: 600 }}>Full Portfolio</Typography>
                          </Box>
                        } 
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </RadioGroup>
            </Box>
            
            {reportLevel === 'project' && (
              <Box sx={{ mb: 5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>2. Choose Project</Typography>
                <FormControl fullWidth>
                  <InputLabel>Select Project</InputLabel>
                  <Select
                    value={selectedProject}
                    label="Select Project"
                    onChange={handleProjectChange}
                    sx={{ borderRadius: 3 }}
                  >
                    {projects.map((project) => (
                      <MenuItem key={project.id} value={project.id ? String(project.id) : ''}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            
            <Box sx={{ mb: 5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>{reportLevel === 'project' ? '3.' : '2.'} Select Format</Typography>
              <RadioGroup row value={reportFormat} onChange={handleFormatChange}>
                <Stack direction="row" spacing={3}>
                  <FormControlLabel 
                    value="pdf" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PdfIcon sx={{ mr: 1, color: '#f44336' }} />
                        <Typography sx={{ fontWeight: 600 }}>PDF Document</Typography>
                      </Box>
                    } 
                  />
                  <FormControlLabel 
                    value="word" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <WordIcon sx={{ mr: 1, color: '#2196f3' }} />
                        <Typography sx={{ fontWeight: 600 }}>Word Document (.docx)</Typography>
                      </Box>
                    } 
                  />
                </Stack>
              </RadioGroup>
            </Box>
            
            <Divider sx={{ mb: 4 }} />
            
            <Button 
              variant="contained" 
              fullWidth
              size="large"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={reportLevel === 'project' && !selectedProject}
              sx={{ 
                height: 64, 
                borderRadius: 4, 
                fontSize: '1.1rem', 
                fontWeight: 800,
                boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.4)'
              }}
            >
              Generate & Download Report
            </Button>
          </Paper>
        </Grid>
        
        <Grid {...{ item: true, xs: 12, md: 4 }}>
          <Stack spacing={4}>
            <Card elevation={0} sx={{ borderRadius: 5, border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(248, 250, 252, 0.5)' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 800 }}>
                  Report Insights
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  Our reports aggregate data from multiple sources to provide a 360° view of your operations.
                </Typography>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>What's included:</Typography>
                  <ul style={{ paddingLeft: '20px', color: '#64748b', fontSize: '0.875rem' }}>
                    <li>Executive Status Summary</li>
                    <li>Task Completion Analytics</li>
                    <li>Project Health Metrics</li>
                    <li>Timeline & Milestones</li>
                    <li>Resource Allocation (BETA)</li>
                  </ul>
                </Box>
              </CardContent>
            </Card>
            
            <Alert severity="info" sx={{ borderRadius: 4 }}>
              <Typography variant="body2" fontWeight={600}>
                Pro Tip: Use PDF for stakeholder presentations and Word if you need to manually edit the report before distribution.
              </Typography>
            </Alert>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ReportsPage;
